import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faPlus,
  faEdit,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import AdminSideMenu from "../../navbar/AdminSideMenu";
import AdminTopMenu from "../../navbar/AdminTopMenu";
import { lowBalanceAPI } from "../../../api/api";

const emptyForm = {
  id: "",
  threshold_percent: "",
  alert_count: "",
  email: "",
  enabled: 1,
};

const LowBalance = () => {
  const { id } = useParams();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [alertBox, setAlertBox] = useState({ show: false, msg: "", cls: "" });

  const notify = (msg, ok) =>
    setAlertBox({
      show: true,
      msg,
      cls: ok ? "alert alert-success" : "alert alert-danger",
    });

  const getList = () => {
    setLoading(true);
    lowBalanceAPI("get", { username: id })
      .then((res) => {
        setLoading(false);
        if (res.data.success === true) {
          setList(res.data.data || []);
        } else {
          setList([]);
        }
      })
      .catch(() => {
        setLoading(false);
        setList([]);
        notify("Unable to load low balance settings", false);
      });
  };

  useEffect(() => {
    getList();
  }, [id]);

  const openAdd = () => {
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      threshold_percent: row.threshold_percent,
      alert_count: row.alert_count,
      email: row.email || "",
      enabled: Number(row.enabled),
    });
    setShowModal(true);
  };

  const isDuplicateThreshold =
    form.threshold_percent !== "" &&
    list.some(
      (row) =>
        Number(row.threshold_percent) === Number(form.threshold_percent) &&
        String(row.id) !== String(form.id)
    );

  const handleSave = (e) => {
    e.preventDefault();
    if (isDuplicateThreshold) return;
    setSaving(true);
    const payload = {
      username: id,
      threshold_percent: Number(form.threshold_percent),
      alert_count: Number(form.alert_count),
      email: form.email.trim(),
      enabled: Number(form.enabled),
    };
    const request = form.id
      ? lowBalanceAPI("update", {
          id: Number(form.id),
          threshold_percent: payload.threshold_percent,
          alert_count: payload.alert_count,
          email: payload.email,
          enabled: payload.enabled,
        })
      : lowBalanceAPI("add", payload);
    request
      .then((res) => {
        setSaving(false);
        if (res.data.success === true) {
          setShowModal(false);
          notify(res.data.message || "Saved successfully", true);
          getList();
        } else {
          notify(res.data.message || "Unable to save", false);
        }
      })
      .catch(() => {
        setSaving(false);
        notify("Unable to save", false);
      });
  };

  const handleDelete = () => {
    setDeleting(true);
    lowBalanceAPI("delete", { id: Number(deleteRow.id) })
      .then((res) => {
        setDeleting(false);
        setDeleteRow(null);
        if (res.data.success === true) {
          notify(res.data.message || "Deleted successfully", true);
          getList();
        } else {
          notify(res.data.message || "Unable to delete", false);
        }
      })
      .catch(() => {
        setDeleting(false);
        setDeleteRow(null);
        notify("Unable to delete", false);
      });
  };

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <AdminSideMenu />

        <div className="layout-page">
          <AdminTopMenu />

          <div className="content-wrapper">
            <div className="container-xxl container-p-y">
              {alertBox.show && (
                <div className={alertBox.cls} role="alert">
                  {alertBox.msg}
                  <button
                    type="button"
                    className="btn-close float-end"
                    onClick={() => setAlertBox({ show: false, msg: "", cls: "" })}
                  ></button>
                </div>
              )}

              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-3">
                    <Link to="/admin/users" className="btn btn-outline-secondary btn-sm">
                      <FontAwesomeIcon icon={faArrowLeft} />
                    </Link>
                    <div>
                      <h5 className="mb-0">Low Balance Alerts</h5>
                      <small className="text-muted">User: {id}</small>
                    </div>
                  </div>
                  <button className="btn btn-primary" onClick={openAdd}>
                    <FontAwesomeIcon icon={faPlus} className="me-1" /> Add Low
                    Balance
                  </button>
                </div>

                <div className="table-responsive text-nowrap">
                  <table className="table">
                    <thead>
                      <tr style={{ backgroundColor: "rgba(105, 108, 255, 0.08)" }}>
                        {/* <th>#</th> */}
                        <th>Username</th>
                        <th>Threshold %</th>
                        <th>Alert Count</th>
                        <th>Email</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={7} className="text-center py-4">
                            Loading...
                          </td>
                        </tr>
                      ) : list.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center py-4 text-muted">
                            No low balance settings found
                          </td>
                        </tr>
                      ) : (
                        list.map((row, i) => (
                          <tr key={row.id}>
                            {/* <td>{i + 1}</td> */}
                            <td>{row.username}</td>
                            <td>{row.threshold_percent}%</td>
                            <td>{row.alert_count}</td>
                            <td>{row.email || "-"}</td>
                            <td>
                              {Number(row.enabled) === 1 ? (
                                <span className="badge bg-label-success">Enabled</span>
                              ) : (
                                <span className="badge bg-label-secondary">Disabled</span>
                              )}
                            </td>
                            <td>{row.created}</td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary me-2"
                                onClick={() => openEdit(row)}
                              >
                                <FontAwesomeIcon icon={faEdit} /> Edit
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => setDeleteRow(row)}
                              >
                                <FontAwesomeIcon icon={faTrash} /> Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {deleteRow && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Delete Low Balance</h5>
                  <button
                    type="button"
                    className="btn-close"
                    disabled={deleting}
                    onClick={() => setDeleteRow(null)}
                  ></button>
                </div>
                <div className="modal-body">
                  Are you sure you want to delete the low balance setting for{" "}
                  <strong>{deleteRow.username}</strong> (threshold{" "}
                  {deleteRow.threshold_percent}%)? This cannot be undone.
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={deleting}
                    onClick={() => setDeleteRow(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={deleting}
                    onClick={handleDelete}
                  >
                    {deleting ? "Deleting..." : "Yes, Delete"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {showModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <form className="modal-content" onSubmit={handleSave}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    {form.id ? "Edit Low Balance" : "Add Low Balance"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Username</label>
                    <input className="form-control" value={id} disabled />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Threshold Percent (%)</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      required
                      className={
                        "form-control" + (isDuplicateThreshold ? " is-invalid" : "")
                      }
                      value={form.threshold_percent}
                      onChange={(e) =>
                        setForm({ ...form, threshold_percent: e.target.value })
                      }
                    />
                    {isDuplicateThreshold && (
                      <div className="invalid-feedback d-block">
                        A {form.threshold_percent}% threshold already exists for
                        this user. Please enter a different value.
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Alert Count</label>
                    <select
                      required
                      className="form-select"
                      value={form.alert_count}
                      onChange={(e) =>
                        setForm({ ...form, alert_count: e.target.value })
                      }
                    >
                      <option value="">Select alert count</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      required
                      className="form-control"
                      placeholder="Enter email"
                      value={form.email}
                      onChange={(e) =>
                        setForm({ ...form, email: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={form.enabled}
                      onChange={(e) =>
                        setForm({ ...form, enabled: e.target.value })
                      }
                    >
                      <option value={1}>Enabled</option>
                      <option value={0}>Disabled</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving || isDuplicateThreshold}>
                    {saving ? "Saving..." : form.id ? "Update" : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default LowBalance;
